/**
 * API service for interacting with the FastAPI backend
 */
import axios from 'axios';
import { getApiBaseUrl, getCdnSiteHeaders, getLanguage, isCdnMode } from '../config/siteMode';

const API_BASE_URL = getApiBaseUrl();

// API key handling:
// - Production internal tool: NO key in the bundle — nginx proxies same-origin
//   /api/* to the backend and injects X-API-Key server-side (behind Basic Auth).
//   A prebuild guard (scripts/assert-no-api-key.js) blocks any production
//   build with REACT_APP_API_KEY set, since CRA embeds every REACT_APP_* var.
// - CDN customers: Shopify-login session cookie, never an API key.
// - Local development (npm start, no nginx): key from .env, dev server only.
const API_KEY = process.env.NODE_ENV === 'development' ? process.env.REACT_APP_API_KEY || '' : '';
const useSessionAuth = isCdnMode();

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: useSessionAuth,
  headers: {
    'Content-Type': 'application/json',
    ...getCdnSiteHeaders(),
    ...(!useSessionAuth && API_KEY && { 'X-API-Key': API_KEY }),
  },
});

export interface RecommendationResult {
  shopify_customer_id: string;
  customer_info: {
    segment?: string;
    tier?: string;
    engagement?: number;
    total_purchases?: number;
    total_spent?: number;
    frequency?: number;
    avg_transaction_value?: number;
  };
  /** Primary payload: carousels keyed by profile (and special keys), pre-ordered by the backend. */
  recommendations_by_profile?: Record<
    string,
    Array<{
      book?: {
        book_id?: string;
        title?: string;
        author?: string;
        price?: number;
        language?: string;
        publisher?: string;
        publication_date?: string;
        page_count?: number;
        isbn?: string;
        sku?: string;
        image_src?: string;
        merchandise_category?: string;
        bisac_subject?: string;
        purchase_count?: number;
        global_purchase_count?: number;
        popularity_score?: number;
        avg_quantity?: number;
        recommendation_type?: string;
        book_embedding?: number[];
        theme1?: string;
        theme2?: string;
        is_canadian_author?: boolean;
        indigo_recommendation_types?: string[];
        BOOK_ID?: string;
        TITLE?: string;
        AUTHOR?: string;
        LIST_PRICE?: number;
        LANGUAGE?: string;
        PUBLISHER?: string;
        ISBN?: string;
        SKU?: string;
        IMAGE_SRC?: string;
        THEME1?: string;
        THEME2?: string;
      };
      recommendation_score: number;
      original_recommendation_score?: number;
      preference_score?: number;
      avg_engagement?: number;
      language?: string;
      genres?: string[];
      themes?: string[];
      sources?: string[];
      explanation?: string;
      method?: string;
      reason?: string;
      purchase_profile?: string;
      matching_profile?: string;
      is_canadian_author?: boolean;
    }>
  >;
  thinking_process: string[];
  filtered_books?: Array<{
    title: string;
    author: string;
    price: number;
    language: string;
    score: number;
    reasoning: string;
    original_explanation: string;
  }>;
  purchase_profiles?: Array<{
    name: string;
    description: string;
    reasoning?: string;
    book_count: number;
    book_indices?: number[];
    book_ids?: string[];
    book_titles?: string[]; // Titles of the books that make up this profile
    characteristics?: string[]; // UI display characteristics
    themes?: string[]; // LLM-selected themes for this profile (profile-first approach)
    genres?: string[]; // LLM-selected genres for this profile (profile-first approach)
    languages?: string[]; // Languages from books in this profile
    for_self?: boolean; // Whether this profile is for the customer themselves
    recipient_type?: 'self' | 'child' | 'partner' | 'gift' | 'unknown'; // Who the books are for
  }>;
  dominant_profiles?: Array<{
    name: string;
    description: string;
    reasoning?: string;
    book_count: number;
    book_indices?: number[];
    book_ids?: string[];
    book_titles?: string[]; // Titles of the books that make up this profile
    characteristics?: string[]; // UI display characteristics
    themes?: string[]; // LLM-selected themes (used for hybrid recommendations)
    genres?: string[]; // LLM-selected genres (used for hybrid recommendations)
    languages?: string[];
    for_self?: boolean;
    recipient_type?: 'self' | 'child' | 'partner' | 'gift' | 'unknown';
  }>;
  /** Full ranked list of all candidates (up to ~250). Ordered by display priority
   *  with explicit rank, score, and source_carousel on each entry. */
  recommendations?: Array<{
    book?: {
      book_id?: string;
      title?: string;
      author?: string;
      price?: number;
      language?: string;
      isbn?: string;
      aggregation_id?: string;
      theme1?: string;
      theme2?: string;
      genres?: string[];
      is_canadian_author?: boolean;
      indigo_recommendation_types?: string[];
    };
    rank?: number;
    recommendation_score?: number;
    source_carousel?: string;
    sources?: string[];
    explanation?: string;
    themes?: string[];
    genres?: string[];
    is_canadian_author?: boolean;
  }>;
  error?: string;
  /** Set by the onboarding (cold-start) flow: "onboarding" vs warm "personalized". */
  personalization_level?: string;
  /** Present on onboarding responses: "ok" | "unavailable". */
  status?: string;
  filtered_purchased_books?: Array<{
    title: string;
    sku: string;
    reason: string;
  }>;
}

export type ProfileRecommendationItem = NonNullable<
  NonNullable<RecommendationResult['recommendations_by_profile']>[string]
>[number];

/** Flatten profile carousels into one list (Object.values order, then each carousel's order). */
export function flattenRecommendationsByProfile(
  byProfile: RecommendationResult['recommendations_by_profile']
): ProfileRecommendationItem[] {
  if (!byProfile) return [];
  return Object.values(byProfile).flat();
}

export interface CustomerHistoryItem {
  book_id?: string;
  title?: string;
  author?: string;
  isbn?: string;
  sku?: string;
  price?: number;
  language?: string;
  publisher?: string;
  theme1?: string;
  theme2?: string;
  is_canadian_author?: boolean;
  interaction_type?: string;
  last_purchase_date?: string;
  purchase_count?: number;
  genres?: string[];
}

export interface CustomerHistoryResponse {
  shopify_customer_id: string;
  history: CustomerHistoryItem[];
  history_count: number;
  created_at: string;
}

export interface HealthResponse {
  status: string;
  database_connected: boolean;
  agent_ready: boolean;
  message?: string;
}

/**
 * Check API health
 */
export const checkHealth = async (): Promise<HealthResponse> => {
  const response = await apiClient.get<HealthResponse>('/health');
  return response.data;
};

/**
 * Response returned when recommendations are queued for async processing.
 */
export interface RecommendationQueuedResponse {
  status: 'processing';
  message: string;
  personalized_url: string;
  shopify_customer_id: string;
}

export type RecommendationByEmailResponse = RecommendationResult | RecommendationQueuedResponse;

/**
 * Type guard to distinguish a queued response from a full result.
 */
export const isQueuedResponse = (
  res: RecommendationByEmailResponse
): res is RecommendationQueuedResponse => {
  return (res as RecommendationQueuedResponse).status === 'processing';
};

/**
 * Get recommendations for a customer by email address.
 *
 * Returns either:
 *  - Full recommendations (cache hit)
 *  - A queued response with status 202 (cache miss — async processing)
 */
export const getRecommendationsByEmail = async (
  email: string
): Promise<RecommendationByEmailResponse> => {
  const response = await apiClient.post('/recommendations/by-email', {
    email: email,
  });
  return response.data;
};

/**
 * Fetch cached recommendations by shopify_customer_id (personalized URL).
 *
 * Returns full results when ready, or a 202-shaped object while processing.
 */
export const getPersonalizedRecommendations = async (
  shopifyCustomerId: string
): Promise<RecommendationByEmailResponse> => {
  const response = await apiClient.get(`/recommendations/${shopifyCustomerId}`);
  return response.data;
};

/**
 * Onboarding (cold-start) types and API functions.
 *
 * New shoppers with no Neo4j presence are served semi-personalized
 * recommendations from stated preferences. Backend endpoints are keyed by
 * shopify_customer_id and isolated from the warm pipeline.
 */
export interface OnboardingOption {
  value: string;
  /** Static options carry an i18n key; dynamic (genre/theme) options carry a label. */
  i18n_key?: string;
  label?: string;
  /** Audiences this option applies to (e.g. ['ADULT']); used to filter genres by recipient. */
  audiences?: string[];
  /** KIDS age bands this genre has shelves for; used to hide kids genres with no shelf for the chosen age. */
  age_bands?: string[];
}

export interface OnboardingQuestion {
  id: string;
  i18n_key: string;
  type: 'single' | 'multi';
  required?: boolean;
  min?: number;
  max?: number;
  /** Recipients this question applies to (e.g. ['kids']); the step is skipped when none are selected. */
  applies_to_recipients?: string[];
  options?: OnboardingOption[];
}

/**
 * GET /onboard response. When `needs_onboarding` is true, `questions` is set.
 * When the shopper already onboarded, the recommendation fields are populated
 * (it spreads a RecommendationResult), so this extends it partially.
 */
export interface OnboardingStateResponse extends Partial<RecommendationResult> {
  needs_onboarding: boolean;
  shopify_customer_id: string;
  warm?: boolean;
  version?: string;
  build_id?: string;
  questions?: OnboardingQuestion[];
}

export interface OnboardRequestBody {
  /** One or more recipients (e.g. ['self','kids']); a bare string is still accepted. */
  recipient_type: string | string[];
  genres: string[];
  themes: string[];
  /** Multiple languages allowed (e.g. ['en','fr']). */
  languages?: string[];
  /** KIDS age band ids (e.g. ['6to8']); only meaningful when 'kids' is a recipient. */
  kids_age?: string[];
}

/** Discovery: returns onboarding questions, existing onboarding recs, or warm=false signal. */
export const getOnboarding = async (
  shopifyCustomerId: string
): Promise<OnboardingStateResponse> => {
  const response = await apiClient.get<OnboardingStateResponse>(`/onboard/${shopifyCustomerId}`);
  return response.data;
};

/** Submit stated preferences and get semi-personalized recommendations. */
export const postOnboarding = async (
  shopifyCustomerId: string,
  body: OnboardRequestBody
): Promise<RecommendationResult> => {
  const response = await apiClient.post<RecommendationResult>(
    `/onboard/${shopifyCustomerId}`,
    body
  );
  return response.data;
};

/**
 * Feedback-related types and API functions
 */
export interface BookRatingItem {
  isbn: string;
  title: string;
  rating: 'thumbs_up' | 'thumbs_down' | null;
  is_gift?: boolean | null;
}

export interface ProfileRatingItem {
  name: string;
  rating: 'thumbs_up' | 'thumbs_down';
}

export interface FeedbackRequest {
  customer_id?: string;
  email?: string;
  overall_rating: number;
  comments: string;
  book_ratings: BookRatingItem[];
  history_book_ratings: BookRatingItem[];
  profile_ratings: ProfileRatingItem[];
  timestamp: string;
}

export interface FeedbackResponse {
  success: boolean;
  feedback_id?: string;
  message: string;
  error?: string;
}

/**
 * Submit user feedback on recommendations
 */
export const submitFeedback = async (feedback: FeedbackRequest): Promise<FeedbackResponse> => {
  const response = await apiClient.post<FeedbackResponse>('/feedback', feedback);
  return response.data;
};

/**
 * Shelf a book from a For You carousel: what the shopper intends to do with it.
 * `null` clears a previous choice.
 */
export type BookShelf = 'want_to_read' | 'already_read' | 'not_for_me';

export interface BookShelfActionRequest {
  book_id: string;
  shelf: BookShelf | null;
  shopify_customer_id: string;
  /** Carousel the book was shown in. */
  carousel_name?: string;
  /** Zero-based position of the book within its carousel. */
  position?: number;
}

export const submitBookShelfAction = async (
  body: BookShelfActionRequest
): Promise<{ success: boolean }> => {
  const response = await apiClient.post<{ success: boolean }>('/feedback/book-shelf', body);
  return response.data;
};

/** Saved shelf picks for a customer, keyed by book_id. */
export const getBookShelfActions = async (
  shopifyCustomerId: string
): Promise<Record<string, BookShelf>> => {
  const response = await apiClient.get<{ shelves: Record<string, BookShelf> }>(
    `/feedback/book-shelf/${encodeURIComponent(shopifyCustomerId)}`
  );
  return response.data.shelves ?? {};
};

/**
 * Two-question feedback on the For You experience (customer-facing popup).
 *
 * The two questions are asked on different scales, so each stores its own
 * wording: a shared yes/somewhat/no would not say which question a bare "yes"
 * had answered.
 */
export type UnderstandingAnswer = 'very_well' | 'somewhat' | 'not_really';

export type BooksFitAnswer = 'yes' | 'some_of_them' | 'not_really';

export interface ExperienceFeedbackRequest {
  shopify_customer_id: string;
  understanding?: UnderstandingAnswer | null;
  books_fit?: BooksFitAnswer | null;
  comment?: string;
  personalization_level?: string;
}

export type AffinityRankingAnswer = 'good' | 'mixed' | 'poor';

/** Reviewer feedback on one /reranker result; stored in `affinity_feedback`. */
export interface AffinityFeedbackRequest {
  kind: 'rate' | 'report';
  shopify_customer_id: string;
  request_key?: string | null;
  ranking?: AffinityRankingAnswer | null;
  comment?: string;
  mode?: string | null;
  top_k?: number | null;
  scorer?: string | null;
  cf_version?: string | null;
  top_book_ids?: string[];
}

export const submitAffinityFeedback = async (
  body: AffinityFeedbackRequest
): Promise<{ success: boolean }> => {
  const response = await apiClient.post<{ success: boolean }>('/feedback/affinity', body);
  return response.data;
};

export const submitExperienceFeedback = async (
  body: ExperienceFeedbackRequest
): Promise<{ success: boolean }> => {
  const response = await apiClient.post<{ success: boolean }>('/feedback/experience', body);
  return response.data;
};

/**
 * Record a book card click/tap in real-time. Stored as an aggregate counter
 * per customer+book on the backend (one document, click_count + last context).
 */
export interface BookClickRequest {
  book_id: string;
  shopify_customer_id: string;
  /** Carousel/profile the book was shown in. */
  carousel_name?: string;
  /** Zero-based position of the book within its carousel. */
  position?: number;
  /** UI surface the click came from (e.g. 'foryou'). */
  source_tab?: string;
}

export const submitBookClick = async (body: BookClickRequest): Promise<{ success: boolean }> => {
  const response = await apiClient.post<{ success: boolean }>('/feedback/book-click', body);
  return response.data;
};

/**
 * Submit a single profile like/dislike rating in real-time (Your Profile tab).
 */
export interface ProfileRatingRequest {
  profile_name: string;
  rating: 'thumbs_up' | 'thumbs_down';
  shopify_customer_id: string;
}

export const submitProfileRating = async (
  body: ProfileRatingRequest
): Promise<{ success: boolean }> => {
  const response = await apiClient.post<{ success: boolean }>('/feedback/profile-rating', body);
  return response.data;
};

/**
 * Get all feedback entries (admin use)
 */
export const getAllFeedback = async (limit: number = 100): Promise<any[]> => {
  const response = await apiClient.get<any[]>(`/feedback?limit=${limit}`);
  return response.data;
};

export interface CoverModerationBook {
  book_id: string;
  title: string;
  author: string;
  sku: string;
  image_src: string | null;
  cover_explicit: boolean | null;
  cover_explicit_reason: string | null;
  cover_moderation_date: string | null;
  cover_moderation_version: string | null;
}

export interface ModerationFeedbackRequest {
  book_id: string;
  feedback: string;
}

export interface ModerationFeedbackResponse {
  success: boolean;
  feedback_id?: string;
  message: string;
}

export interface CoverModerationStat {
  explicit: boolean;
  count: number;
}

export interface CoverModerationResponse {
  books: CoverModerationBook[];
  stats: CoverModerationStat[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * Get cover moderation results for visual verification
 */
export const getCoverModerationData = async (
  limit: number = 100,
  offset: number = 0,
  status?: string
): Promise<CoverModerationResponse> => {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (status) params.set('status', status);
  const response = await apiClient.get<CoverModerationResponse>(
    `/moderation/covers?${params.toString()}`
  );
  return response.data;
};

/**
 * Submit moderator feedback for a book's cover moderation result
 */
export const submitModerationFeedback = async (
  request: ModerationFeedbackRequest
): Promise<ModerationFeedbackResponse> => {
  const response = await apiClient.post<ModerationFeedbackResponse>(
    '/moderation/feedback',
    request
  );
  return response.data;
};

/**
 * Fetch cached customer purchase/browse history by shopify_customer_id.
 *
 * The history is saved to Firestore during recommendation generation
 * and served from the /customer/history/:shopify_customer_id endpoint.
 */
export const getCustomerHistory = async (
  shopifyCustomerId: string
): Promise<CustomerHistoryResponse> => {
  const response = await apiClient.get<CustomerHistoryResponse>(
    `/customer/history/${shopifyCustomerId}`
  );
  return response.data;
};

/**
 * Customer authentication (CDN mode — Shopify Customer Account login)
 */
export interface AuthMeResponse {
  authenticated: boolean;
  shopify_customer_id?: string;
  language?: string;
  first_name?: string;
}

/** Session status from the signed HTTP-only cookie. */
export const getAuthMe = async (): Promise<AuthMeResponse> => {
  const response = await apiClient.get<AuthMeResponse>('/auth/me');
  return response.data;
};

/** Full-page redirect target that starts the Shopify hosted sign-in flow. */
export const getLoginUrl = (returnTo: string = '/'): string => {
  const params = new URLSearchParams({ lang: getLanguage(), return_to: returnTo });
  return `${API_BASE_URL}/auth/login?${params.toString()}`;
};

export interface LogoutResponse {
  success: boolean;
}

/** Clear the customer session cookie. */
export const logoutCustomer = async (): Promise<LogoutResponse> => {
  const response = await apiClient.post<LogoutResponse>('/auth/logout');
  return response.data;
};

// ─── Book affinity (internal /reranker tool) ─────────────────────────────────

export type AffinityMode = 'retrieve' | 'book_ids';

export interface AffinityScoreRequest {
  shopify_customer_id: string;
  mode: AffinityMode;
  book_ids?: string[];
  top_k?: number;
  force?: boolean;
}

export type AffinityFlag =
  | 'content_blocked'
  | 'age_mismatch'
  | 'off_genre'
  | 'owned'
  | 'vector_missing';

export interface AffinityBookRow {
  book_id: string;
  title?: string | null;
  author?: string | null;
  sku?: string | null;
  age_group?: string | null;
  primary_subject?: string | null;
  series_name?: string | null;
  score: number;
  rank: number;
  winning_signal: 'cf' | 'desc' | 'metadata';
  features: Record<string, number | null>;
  flags: AffinityFlag[];
  excluded_by_pipeline: boolean;
}

/** `rank` scores are unbounded ranking-model margins, not probabilities. */
export type AffinityScoreType = 'probability' | 'rank' | 'similarity';

export interface AffinityDocument {
  shopify_customer_id: string;
  request_key: string;
  mode: AffinityMode;
  status: 'ready' | 'no_customer_vectors' | 'error';
  scorer?: string;
  score_type?: AffinityScoreType;
  cf_version?: string | null;
  customer?: {
    has_cf_user_vector: boolean;
    has_desc_vector: boolean;
    desc_vector_book_count: number;
    audience_mix: Record<string, number>;
    top_level_genres: string[];
    top_themes: string[];
    avg_log_popularity: number | null;
    language_mix?: Record<string, number>;
    avg_log_price?: number | null;
    format_mix?: Record<string, number>;
    avg_log_pages?: number | null;
    owned_count: number;
  } | null;
  books?: AffinityBookRow[];
  unresolved_book_ids?: string[];
  scored_at?: string;
  execution_ms?: number;
  error?: string;
}

/** Served from the day-long cache, or scored by the API on a miss. */
export const postAffinity = async (body: AffinityScoreRequest): Promise<AffinityDocument> => {
  const response = await apiClient.post<AffinityDocument>('/affinity', body);
  return response.data;
};
